import { useEffect, useState } from 'react';

const usePositionBodySnippet = ({ titleBodyComponent, item }) => {
  const { highlights, body } = item ?? {};
  const [shouldTrimContent, setShouldTrimContent] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  // Check if highlighted element is on a hidden line (beyond line-clamp)
  useEffect(() => {
    if (highlights?.highlightedBodySnippets[0] && titleBodyComponent?.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const container = titleBodyComponent.current;
        const highlightedElement = container?.querySelector('em.policymogul-keyword-match');

        if (highlightedElement && container) {
          const containerHeight = container.clientHeight;
          const containerWidth = container.clientWidth;
          const highlightTop = highlightedElement.offsetTop;
          const highlightHeight = highlightedElement.clientHeight;

          // Check if highlight is beyond the visible area
          const isHidden = highlightTop + highlightHeight > containerHeight;
          setShouldTrimContent(isHidden);
          setContainerWidth(containerWidth);
        }
      }, 50);
    } else {
      setShouldTrimContent(false);
    }
  }, [highlights?.highlightedBodySnippets, item?.id]);

  // Trim highlighted snippet to show the highlighted word at the start
  const trimToHighlight = (htmlString) => {
    if (!htmlString) return htmlString;

    // Find the position of common highlight tags
    const highlightPatterns = ['<em>', '<em '];
    let firstHighlightPos = -1;

    for (const pattern of highlightPatterns) {
      const pos = htmlString.indexOf(pattern);
      if (pos !== -1 && (firstHighlightPos === -1 || pos < firstHighlightPos)) {
        firstHighlightPos = pos;
      }
    }

    if (firstHighlightPos === -1) return htmlString;

    // Keep much more context before the highlight to preserve content
    const contextLength = containerWidth ? Math.floor(containerWidth / 5 - (containerWidth < 300 ? 20 : 0)) : 150; // characters of context before highlight
    const startPos = Math.max(0, firstHighlightPos - contextLength);

    if (startPos === 0) return htmlString;

    // Find a good breaking point - prefer sentence boundaries, then word boundaries
    let trimPos = startPos;

    // First, look for a sentence boundary (period followed by space)
    for (let i = startPos; i < firstHighlightPos - 1; i++) {
      if (htmlString[i] === '.' && htmlString[i + 1] === ' ') {
        trimPos = i + 2; // Start after the period and space
      }
    }

    // If no sentence boundary found, look for word boundaries but keep lots of context
    if (trimPos === startPos) {
      for (let i = startPos; i < startPos + 30; i++) {
        if (i < firstHighlightPos && (htmlString[i] === ' ' || htmlString[i] === '>')) {
          trimPos = i + 1;
          break;
        }
      }
    }

    // Only add ellipsis if we actually trimmed content
    const prefix = trimPos > 0 ? '...' : '';
    return prefix + htmlString.substring(trimPos);
  };

  let bodySnippet =
    highlights?.highlightedBodySnippets[0] && shouldTrimContent
      ? trimToHighlight(highlights.highlightedBodySnippets[0])
      : highlights?.highlightedBodySnippets[0] ?? body;

  // Move full stop inside em tag if it appears after closing tag
  if (bodySnippet && typeof bodySnippet === 'string') {
    bodySnippet = bodySnippet.replace(/<\/em>\./g, '.</em>');
  }

  return { bodySnippet };
};

export default usePositionBodySnippet;
